'use client';

import { useState } from 'react';
import { Package, Warehouse, AlertTriangle, TrendingDown, ArrowRight, PackageX, ArrowLeftRight } from 'lucide-react';
import { NumberCard } from '../../../components/shared/NumberCard';
import { DataTable } from '../../../components/shared/DataTable';
import { warehouses, stockBalances, stockEntries, getLowStockItems } from '../data/mock-data';
import { calculateWarehouseUsage, STOCK_ENTRY_TYPE_LABELS } from '../types';
import type { StockBalance, StockEntry, StockEntryType } from '../types';
import { formatCurrency } from '../../../types';

import type { StockPage } from '../index';

type TabType = 'lowStock' | 'entries';

interface StockDashboardProps {
  onNavigate: (page: StockPage) => void;
}

export function StockDashboard({ onNavigate }: StockDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>('lowStock');
  const lowStockItems = getLowStockItems();
  const outOfStockCount = stockBalances.filter((sb) => sb.quantity === 0).length;
  const totalItems = stockBalances.length;
  const recentEntries = stockEntries.slice(0, 5);

  // 전체 재고 가치 계산 (단순화)
  const totalStockValue = stockBalances.reduce(
    (sum, sb) => sum + sb.quantity * 10000, // 평균 단가로 계산
    0
  );

  return (
    <div className="space-y-6">
      {/* 요약 카드 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <NumberCard
          title="전체 품목"
          value={totalItems}
          subtitle={`${warehouses.length}개 창고`}
          icon={<Package className="w-4 h-4" />}
          iconClassName="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
        />
        <NumberCard
          title="재고 가치"
          value={formatCurrency(totalStockValue)}
          icon={<Warehouse className="w-4 h-4" />}
          iconClassName="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
        />
        <NumberCard
          title="재고 부족"
          value={lowStockItems.length}
          subtitle="발주 필요"
          icon={<TrendingDown className="w-4 h-4" />}
          iconClassName="bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400"
          trend={lowStockItems.length > 0 ? { value: -10, label: '전주 대비' } : undefined}
        />
        <NumberCard
          title="품절"
          value={outOfStockCount}
          subtitle="긴급 발주 필요"
          icon={<AlertTriangle className="w-4 h-4" />}
          iconClassName="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
        />
      </div>

      {/* 창고별 현황 */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">창고별 현황</h2>
          <button
            onClick={() => onNavigate('warehouses')}
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            전체 보기 <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {warehouses.map((warehouse) => {
            const usage = calculateWarehouseUsage(warehouse);
            return (
              <div
                key={warehouse.id}
                className="p-4 rounded-lg border border-border hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => onNavigate('warehouses')}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">{warehouse.name}</h3>
                  <span className="text-sm text-muted-foreground">{warehouse.code}</span>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">사용률</span>
                    <span className={usage > 80 ? 'text-red-500' : usage > 60 ? 'text-yellow-500' : 'text-green-500'}>
                      {usage}%
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        usage > 80 ? 'bg-red-500' : usage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${usage}%` }}
                    />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {warehouse.usedCapacity.toLocaleString()} / {warehouse.capacity.toLocaleString()} 단위
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* 탭 형태 테이블 섹션 */}
      <div className="bg-card border border-border rounded-lg">
        {/* 탭 헤더 */}
        <div className="flex items-center justify-between border-b border-border">
          <div className="flex">
            <button
              onClick={() => setActiveTab('lowStock')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'lowStock'
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <PackageX className="w-4 h-4" />
              재고 부족
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
                activeTab === 'lowStock'
                  ? 'bg-red-500 text-white'
                  : 'bg-muted text-muted-foreground'
              }`}>{lowStockItems.length}</span>
            </button>
            <button
              onClick={() => setActiveTab('entries')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'entries'
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <ArrowLeftRight className="w-4 h-4" />
              최근 이동
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
                activeTab === 'entries'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>{recentEntries.length}</span>
            </button>
          </div>
          <button
            onClick={() => onNavigate(activeTab === 'lowStock' ? 'balance' : 'entries')}
            className="text-sm text-primary hover:underline flex items-center gap-1 pr-4"
          >
            전체 보기 <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* 탭 컨텐츠 */}
        <div className="p-4">
          {activeTab === 'lowStock' ? (
            lowStockItems.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                재고 부족 품목이 없습니다
              </p>
            ) : (
              <DataTable
                data={lowStockItems}
                columns={[
                  { key: 'itemName', label: '품목명', width: '50%' },
                  { key: 'warehouseName', label: '창고', width: '30%' },
                  {
                    key: 'quantity',
                    label: '현재 재고',
                    width: '20%',
                    align: 'right' as const,
                    render: (value: unknown) => (
                      <span className="text-red-500 font-medium">{value as number}</span>
                    ),
                  },
                ]}
                pageSize={5}
                showPagination={false}
              />
            )
          ) : (
            <DataTable
              data={recentEntries}
              columns={[
                {
                  key: 'type',
                  label: '유형',
                  width: '15%',
                  render: (value: unknown) => (
                    <EntryTypeBadge type={value as StockEntryType} />
                  ),
                },
                { key: 'itemName', label: '품목', width: '40%' },
                {
                  key: 'quantity',
                  label: '수량',
                  width: '20%',
                  align: 'right' as const,
                  render: (value: unknown, row: unknown) => {
                    const entry = row as StockEntry;
                    const isNegative = entry.type === 'issue' || (entry.type === 'adjustment' && (value as number) < 0);
                    return (
                      <span className={isNegative ? 'text-red-500' : 'text-green-500'}>
                        {isNegative ? '' : '+'}{value as number}
                      </span>
                    );
                  },
                },
                { key: 'date', label: '날짜', width: '25%' },
              ]}
              pageSize={5}
              showPagination={false}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function EntryTypeBadge({ type }: { type: StockEntryType }) {
  const colors: Record<StockEntryType, string> = {
    receive: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    issue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    transfer: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    adjustment: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[type]}`}>
      {STOCK_ENTRY_TYPE_LABELS[type]}
    </span>
  );
}
